// ③ 支払明細 一覧（デザイン正本 list.jsx 準拠 ＋ 月別アーカイブ docs/29）
// MoneyForward 機能は撤去。承認用に発行は維持。月セレクタ・月を確定・過去月読み取り・再オープンに対応。

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Ic } from "../components/Icons";
import { useStore } from "../store";
import { BulkPrintSheets, preloadPrintAssets } from "./StatementDetail";
import { yen } from "../lib/normalize";
import { publishApprovals, fetchApprovalStatus, type PublishedItem, type ApprovalSummary, type ApprovalStatus } from "../lib/approvalApi";
import { isRetiredDriver } from "../lib/driverStatus";
import { matchesFilters, needsAttention, type StatementFilters, type StatementStatusFilter } from "../lib/statementFilter";
import { groupAggregatedMembers } from "../lib/calc/groupSum";
import { buildAccountingCsv } from "../lib/accountingCsv";
import { buildEmailByDriverId } from "../lib/notifyEmail";
import { detectPeriodMismatch, periodMismatchMessage } from "../lib/csv/periodCheck";
import type { Carrier, Statement } from "../types";

const APPROVAL_LABEL: Record<ApprovalStatus, string> = {
  published: "未読",
  viewed: "閲覧済",
  approved: "承認済",
  question: "質問あり",
};
const APPROVAL_COLOR: Record<ApprovalStatus, string> = {
  published: "#8a93a0",
  viewed: "#2b6cb0",
  approved: "#1f8a4c",
  question: "#c2410c",
};

const CARRIER_LABEL: Record<Carrier, string> = { yamato: "ヤマト", sagawa: "佐川" };

const AVATAR_PALETTE: [string, string][] = [
  ["#e2ecfb", "#1d4ed8"], ["#fbf2cf", "#8a6d12"], ["#e7f4ec", "#1f8a4c"], ["#fdecea", "#cc2f2a"], ["#ede8fb", "#6d3fd1"],
];
function avatarColor(name: string): [string, string] {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[h];
}

const keyOf = (s: Statement): string => s.driverId;

// 0円の通知書（売上・控除とも実績なし）。配信除外の対象にできる（本城さん要望 2026-08-10）
const isZeroStatement = (s: Statement): boolean =>
  s.salesTotal === 0 && s.deductTotal === 0 && s.netTotal === 0;

// 要確認の定義とタブのフィルタキーは lib/statementFilter.ts に集約
// （個人明細の前後ナビと同じ判定を使うため。本城さん要望 2026-08-24）

function formatMonth(ym: string): string {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split("-").map(Number);
  return `${y}年${m}月`;
}

// 月セレクタの補足表示。もともと「（作業中）」は "いま開いている月" の意味だったが、
// 確定済みの月でもそう出るため「保存できていないのでは」と読まれた（本城さんの質問 2026-09-01）。
// 確定した月がロックされるようになった今は、状態をそのまま言う:
// 確定済み＝編集不可なら「（確定済）」、訂正のために解除中なら「（訂正中）」、未確定なら「（作業中）」。
function monthOptionSuffix(
  o: { month: string; finalized: boolean },
  targetMonth: string,
  locked: boolean
): string {
  if (o.month !== targetMonth) return o.finalized ? "（確定済）" : "";
  if (!o.finalized) return "（作業中）";
  return locked ? "（確定済）" : "（訂正中）";
}

// 手動明細の作成モーダル：CSVに出ないドライバー（橋本光弘様等）の通知書を空で作る
function ManualCreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (driverId: string) => void }) {
  const { state, dispatch } = useStore();
  const [q, setQ] = useState("");
  const existing = new Set(state.statements.map((s) => s.driverId));
  const candidates = state.drivers
    .filter((d) => !isRetiredDriver(d) && !existing.has(d.id))
    .filter((d) => !q.trim() || d.name.includes(q) || d.id.includes(q) || (d.branch || "").includes(q));
  const create = (id: string) => {
    dispatch({ type: "ADD_MANUAL_STATEMENT", driverId: id });
    onCreated(id);
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <div className="card-title">通知書を手動作成</div>
          <button className="btn btn-icon-only ghost" onClick={onClose}><Ic.x size={16} /></button>
        </div>
        <div className="modal-body col gap-12">
          <div className="tiny muted">
            CSVに出ないドライバー（橋本光弘様など）の通知書を空の状態で作成します。
            金額は明細画面の「調整・臨時売上」（全日／半日／時間超過／距離超過／高速代 など）で入力してください。
          </div>
          <input className="input" placeholder="氏名 / ID / 支店で検索" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="tbl-scroll" style={{ maxHeight: 320 }}>
            <table className="tbl">
              <tbody>
                {candidates.slice(0, 30).map((d) => (
                  <tr key={d.id}>
                    <td className="mono muted" style={{ width: 90 }}>{d.id}</td>
                    <td style={{ fontWeight: 700 }}>{d.name}</td>
                    <td className="tiny muted">{d.branch || "—"}</td>
                    <td style={{ width: 90 }}><button className="btn sm primary" onClick={() => create(d.id)}>作成</button></td>
                  </tr>
                ))}
                {candidates.length === 0 && (
                  <tr><td className="muted" style={{ textAlign: "center", padding: 16 }}>該当なし（既に明細がある人・退職者は選べません）</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export function StatementListScreen({
  onNav,
  onSelect,
  onAddMaster,
  filters,
  onFilters,
}: {
  onNav: (s: string) => void;
  onSelect: (driverId: string) => void;
  onAddMaster?: (name: string) => void;
  filters: StatementFilters;
  onFilters: (f: StatementFilters) => void;
}) {
  const { state, dispatch, displayStatements, isArchive, viewingMonth, monthIndex, archivedRecord, archiveBusy, finalizeMonth, viewMonth, reopenMonth, monthLocked, unlockInfo, unlockMonth, share, refreshShared } = useStore();
  // 絞り込み条件は App 側で保持（画面を離れても消えない）
  const { query, carrier: carrierFilter, status: statusFilter } = filters;
  const setQuery = (v: string) => onFilters({ ...filters, query: v });
  const setCarrierFilter = (v: Carrier | "") => onFilters({ ...filters, carrier: v });
  const setStatusFilter = (v: StatementStatusFilter) => onFilters({ ...filters, status: v });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [flash, setFlash] = useState<{ ok: boolean; msg: string } | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // 「最新の状態にする」（本城さん要望 2026-09-09）。
  // 共有（他の方が保存した内容）と月の確定状況を取り直す。通常は20秒ごとの自動更新で揃うが、
  // 「いまこの画面が最新か」を自分の操作で確かめられるようにする。
  // 手元に未保存の変更があるときは黙って捨てず、画面上の帯でどちらを残すか選んでもらう。
  const handleRefreshShared = async () => {
    setRefreshing(true);
    const r = await refreshShared();
    setRefreshing(false);
    if (!r.ok) setFlash({ ok: false, msg: r.error || "最新の取得に失敗しました" });
    else if (r.changed) setFlash({ ok: true, msg: "共有の最新の内容を取り込みました" });
    else setFlash({ ok: true, msg: "この画面は最新の状態です" });
  };

  // 承認フロー（#6）
  const [approvalMap, setApprovalMap] = useState<Map<string, ApprovalSummary>>(new Map());
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishResult, setPublishResult] = useState<PublishedItem[] | null>(null);
  // 手動明細（CSVに出ないドライバーの通知書。橋本様等）
  const [manualOpen, setManualOpen] = useState(false);

  const statements = displayStatements; // 作業中 or 過去月スナップショット

  const loadApprovalStatus = async () => {
    const r = await fetchApprovalStatus(viewingMonth);
    if (r.ok) {
      const m = new Map<string, ApprovalSummary>();
      for (const it of r.items) m.set(it.driverId, it);
      setApprovalMap(m);
    } else {
      setApprovalMap(new Map());
    }
  };
  useEffect(() => {
    setSelected(new Set()); // 月を切り替えたら選択解除
    if (statements.length > 0) void loadApprovalStatus();
    else setApprovalMap(new Map());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingMonth, statements.length]);

  const filtered = useMemo(() => {
    return statements.filter((s) => matchesFilters(s, filters)).sort((a, b) => b.netTotal - a.netTotal);
  }, [statements, filters]);

  const totals = filtered.reduce(
    (a, s) => { a.sales += s.salesTotal; a.deduct += s.deductTotal; a.net += s.netTotal; return a; },
    { sales: 0, deduct: 0, net: 0 }
  );

  const counts: Record<string, number> = {};
  for (const s of statements) counts[s.status] = (counts[s.status] || 0) + 1;
  const attentionCount = statements.filter(needsAttention).length;
  const confirmedCount = counts["confirmed"] || 0;
  const allConfirmed = statements.length > 0 && confirmedCount === statements.length;

  // 月インデックスの現在月エントリ（確定済みか）
  const currentEntry = monthIndex.find((e) => e.month === state.targetMonth);
  // 月セレクタの選択肢：作業中の月 ＋ アーカイブ済みの月
  const monthOptions = useMemo(() => {
    const set = new Map<string, { month: string; finalized: boolean }>();
    set.set(state.targetMonth, { month: state.targetMonth, finalized: !!currentEntry });
    for (const e of monthIndex) if (!set.has(e.month)) set.set(e.month, { month: e.month, finalized: true });
    return [...set.values()].sort((a, b) => (a.month < b.month ? 1 : a.month > b.month ? -1 : 0));
  }, [monthIndex, state.targetMonth, currentEntry]);

  // 取込CSVの対象月ズレ（本城さんの質問 2026-09-01）。片方のキャリアだけ差し替えると
  // 前月と当月が混ざった明細になる。金額が狂ったまま確定・発行されるのを防ぐ。
  const periodMismatch = isArchive ? null : detectPeriodMismatch(state.imports.yamato?.meta, state.imports.sagawa?.meta);

  const unmatched = isArchive ? [] : [
    ...(state.matches.sagawa?.filter((m) => m.status === "unmatched") || []),
    ...(state.matches.yamato?.filter((m) => m.status === "unmatched") || []),
  ];
  const candidates = isArchive ? [] : [
    ...(state.matches.sagawa?.filter((m) => m.status === "candidate") || []),
    ...(state.matches.yamato?.filter((m) => m.status === "candidate") || []),
  ];
  const unmatchedNames = dedupeNames(unmatched.map((m) => m.csvName));

  // グループ計上の見える化（docs/33）: 各グループルールについて、
  // 実際にCSVに登場する（＝各ドライバーの個人通知書とは別に、グループ宛通知書にも計上される）メンバーを表示する。
  const yamatoRowsAll = state.imports.yamato?.yamatoRows ?? [];
  const sagawaRowsAll = state.imports.sagawa?.sagawaRows ?? [];
  const groupNotes = isArchive
    ? []
    : state.groupRules
        .map((r) => ({ rule: r, members: groupAggregatedMembers(r, yamatoRowsAll, sagawaRowsAll) }))
        .filter((g) => g.members.length > 0);

  // 選択操作。過去月（読み取り専用）でも選べる — まとめてPDF の対象を絞るため（本城さん要望 2026-09-08）。
  // 過去月では選択は「まとめてPDF」の対象指定にだけ使い、確定・発行などの編集操作は出さない。
  const allFilteredSelected = filtered.length > 0 && filtered.every((s) => selected.has(keyOf(s)));
  const toggleAll = () => {
    const ns = new Set(selected);
    if (allFilteredSelected) filtered.forEach((s) => ns.delete(keyOf(s)));
    else filtered.forEach((s) => ns.add(keyOf(s)));
    setSelected(ns);
  };
  const toggleOne = (s: Statement) => {
    const k = keyOf(s);
    const ns = new Set(selected);
    if (ns.has(k)) ns.delete(k);
    else ns.add(k);
    setSelected(ns);
  };
  const clearSelection = () => setSelected(new Set());

  // 選択した明細を確定（明細ごとに確定 → 全員揃ったら月を確定）
  const confirmSelected = () => {
    const targets = state.statements.filter((s) => selected.has(keyOf(s)) && s.status !== "confirmed");
    if (targets.length === 0) return;
    const nowStamp = stamp();
    for (const s of targets) {
      dispatch({ type: "UPDATE_STATEMENT", stmt: { ...s, status: "confirmed", confirmedAt: nowStamp, confirmedBy: "ICB" } });
    }
    setFlash({ ok: true, msg: `${targets.length} 件を確定しました` });
    clearSelection();
  };

  // 承認用に発行
  const publishTargets = selected.size > 0 ? state.statements.filter((s) => selected.has(keyOf(s))) : filtered;
  const handlePublish = async () => {
    if (publishTargets.length === 0) return;
    if (periodMismatch && !window.confirm(`⚠️ ${periodMismatchMessage(periodMismatch)}

この金額のまま承認用に発行（ドライバーへメール送信）しますか？`)) return;
    if (!window.confirm(`${publishTargets.length} 名分の支払明細を「承認用」に発行します。\n各ドライバー専用のURLが作られます（既に発行済みの人はURLそのまま・内容を最新化）。\n\n続けますか？`)) return;
    setPublishBusy(true);
    setPublishResult(null);
    try {
      // グループ宛通知書もここでメール送信先を得る（マスタに行が無いのでグループ設定から引く）
      const emailByDriverId = buildEmailByDriverId(state.drivers, state.groupRules);
      const r = await publishApprovals(viewingMonth, publishTargets, emailByDriverId);
      if (!r.ok || !r.published) { alert(`発行に失敗しました:\n${r.error || "不明なエラー"}`); return; }
      setPublishResult(r.published);
      if (r.mail) {
        if (!r.mail.configured) {
          setFlash({ ok: true, msg: `承認用URLを発行しました。メール未設定のため、URLは手動で配布してください（設定でメール送信を有効化できます）。` });
        } else {
          setFlash({
            ok: r.mail.failed === 0,
            msg: `承認用URLを発行。メール送信 ${r.mail.sent} 件` +
              (r.mail.failed ? ` ／ 失敗 ${r.mail.failed} 件` : "") +
              (r.mail.skipped ? ` ／ メール未登録 ${r.mail.skipped} 件` : ""),
          });
        }
      }
      await loadApprovalStatus();
    } finally {
      setPublishBusy(false);
    }
  };

  // MF会計取込用CSVをダウンロード（Issue #51・本城さん確定 2026-07-23）。表示中の月の全通知書が対象（フィルタ前）。
  const handleDownloadAccountingCsv = () => {
    if (statements.length === 0) return;
    const csv = buildAccountingCsv(statements);
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `HSD支払一覧_${viewingMonth}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ===== 一括PDF出力（本城さん要望 2026-08-06）=====
  // 選択があれば選択分、無ければ表示中すべてを1回の印刷にまとめる。
  // 印刷ダイアログで「PDFに保存」を選ぶと、全員分が1つのPDFになる。
  const bulkTargets = selected.size > 0 ? filtered.filter((s) => selected.has(keyOf(s))) : filtered;
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPrint, setBulkPrint] = useState<{ mode: "notice" | "sales"; items: Statement[] } | null>(null);

  const startBulkPrint = async (mode: "notice" | "sales") => {
    if (bulkTargets.length === 0) return;
    setBulkBusy(true);
    await preloadPrintAssets(); // ロゴ未ロードのまま印刷するとロゴが欠ける
    setBulkPrint({ mode, items: bulkTargets });
  };

  // 印刷用DOMがブラウザに描画されてから印刷ダイアログを出す（描画前に print すると白紙になる）。
  // 後始末は afterprint で行う（print() が即座に返るブラウザで、印刷前にDOMを外さないため）。
  useEffect(() => {
    if (!bulkPrint) return;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setBulkPrint(null);
      setBulkBusy(false);
      setBulkOpen(false);
    };
    window.addEventListener("afterprint", finish);
    let timer: number | undefined;
    const raf = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.print();
        timer = window.setTimeout(finish, 1000); // afterprint 未対応ブラウザの保険
      });
    });
    return () => {
      window.removeEventListener("afterprint", finish);
      window.cancelAnimationFrame(raf);
      if (timer) window.clearTimeout(timer);
    };
  }, [bulkPrint]);

  // 月を確定（スナップショット保存）
  const handleFinalize = async () => {
    if (!allConfirmed) return;
    if (periodMismatch && !window.confirm(`⚠️ ${periodMismatchMessage(periodMismatch)}

このまま ${formatMonth(state.targetMonth)} として確定・保存しますか？`)) return;
    if (!window.confirm(`${formatMonth(state.targetMonth)} の支払明細 ${statements.length} 件を確定し、この月のスナップショットを保存します。\n（保存後も「再オープン」で訂正できます）\n\n続けますか？`)) return;
    const r = await finalizeMonth("ICB");
    setFlash(r.ok
      ? { ok: true, msg: `${formatMonth(state.targetMonth)} を確定・保存しました。過去月としていつでも閲覧・PDF再出力できます。` }
      : { ok: false, msg: `保存に失敗しました：${r.error || "不明なエラー"}（本番Cloudflare環境でのみ保存できます）` });
  };

  // 確定した月の訂正（本城さん要望 2026-09-01）。
  // 押すまでは編集できない。押した記録（誰がいつ）は共有され、全員の画面が編集可能になる。
  const handleUnlock = () => {
    if (!window.confirm(
      `${formatMonth(state.targetMonth)} は確定済みです。訂正するため、編集できる状態に戻します。

` +
      `・この操作は事務所の全員の画面に反映されます
` +
      `・訂正が終わったら、もう一度「月を確定」を押してください（保存し直して再びロックされます）
・訂正中に再計算が起きると、確定後に変更したマスター・単価設定が反映されます（金額が変わることがあります）

続けますか？`
    )) return;
    unlockMonth("ICB");
    setFlash({ ok: true, msg: `${formatMonth(state.targetMonth)} を編集できる状態にしました。訂正後は「月を確定」で保存し直してください。` });
  };

  // 過去月の再オープン（訂正）
  const handleReopen = async () => {
    if (!archivedRecord) return;
    // 再オープンは「作業中の月」をまるごと置き換える。共有中は全員の画面が切り替わり、
    // 作業中だった月が未確定ならどこにも残らないため、そこを明示して確認する。
    const workingCount = state.statements.length;
    const workingFinalized = monthIndex.some((e) => e.month === state.targetMonth);
    const warn =
      workingCount > 0 && !workingFinalized && state.targetMonth !== archivedRecord.month
        ? `\n\n⚠️ いま作業中の ${formatMonth(state.targetMonth)}（${workingCount} 件・未確定）は、この操作で置き換わります。\n共有しているため、他の方の画面も同じように切り替わります。\n（置き換わる前の内容は1世代だけ保管され、設定画面から戻せます）`
        : "";
    if (!window.confirm(`${formatMonth(archivedRecord.month)} を再オープンして訂正します。\nこの月の確定スナップショットを「作業中」に読み込みます。${warn}\n\n続けますか？`)) return;
    const r = await reopenMonth(archivedRecord.month);
    if (r.ok) setFlash({ ok: true, msg: `${formatMonth(archivedRecord.month)} を再オープンしました。訂正後は再度「月を確定」で上書き保存してください。` });
    else setFlash({ ok: false, msg: `再オープンに失敗しました：${r.error || "不明なエラー"}` });
  };

  const onMonthSelect = async (month: string) => {
    setFlash(null);
    const r = await viewMonth(month);
    if (!r.ok) setFlash({ ok: false, msg: r.error || "この月のデータを開けませんでした" });
  };

  // ===== 0円通知書の配信除外（本城さん要望 2026-08-10）=====
  // マスタに名前だけ登録され0円の通知書が出る人（此原様・淺田様＝AT-STYLE経由で支払う人）を
  // 一覧・承認用発行・まとめてPDFから外す。金額が発生した月は自動で復活し、警告が出る。
  const excludeZero = (s: Statement) => {
    if (!window.confirm(
      `${s.driverName} さんの 0円 の通知書を配信対象から外します。\n` +
      `一覧・承認用の発行・まとめてPDFに出なくなります（マスタからは消えません）。\n` +
      `売上や控除が発生した月は自動で通知書が復活し、警告でお知らせします。\n\n続けますか？`
    )) return;
    dispatch({ type: "EXCLUDE_DRIVER", driverId: s.driverId });
    // 手動作成の通知書は除外より優先されて残り続けるため、あわせて手動明細も削除する
    if (state.manualDriverIds.includes(s.driverId)) {
      dispatch({ type: "REMOVE_MANUAL_STATEMENT", driverId: s.driverId });
    }
    setFlash({ ok: true, msg: `${s.driverName} さんを配信除外しました（下の「配信除外中」から戻せます）` });
  };
  const restoreExcluded = (driverId: string, name: string) => {
    dispatch({ type: "RESTORE_EXCLUDED_DRIVER", driverId });
    setFlash({ ok: true, msg: `${name} さんの配信除外を解除しました` });
  };
  // 当月CSVでの実績個数も出す：単価未設定だと実績があっても0円のまま除外され続けるため、
  // 「意図した除外（AT-STYLE経由等）」と「事故（単価未設定のまま除外）」を目で区別できるようにする
  const excludedEntries = state.excludedDriverIds.map((id) => {
    const d = state.drivers.find((x) => x.id === id);
    let qty = 0;
    if (d) {
      for (const r of yamatoRowsAll) if (r.driverNameNormalized === d.nameNormalized) qty += r.takkyubinQty + r.nekoposQty;
      for (const r of sagawaRowsAll) if (r.driverNameNormalized === d.nameNormalized) qty += r.countQty;
    }
    return { id, name: d?.name || id, qty };
  });

  return (
    <div className="content list-content">
      {manualOpen && !isArchive && (
        <ManualCreateModal
          onClose={() => setManualOpen(false)}
          onCreated={(driverId) => {
            setManualOpen(false);
            setFlash({ ok: true, msg: "通知書を手動作成しました。明細画面の「調整・臨時売上」で金額を入力してください。" });
            onSelect(driverId);
          }}
        />
      )}
      <div className="page-title-row">
        <div className="col gap-4">
          <div className="row gap-12" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <div className="page-title">支払明細 一覧</div>
            <select
              className="select"
              value={viewingMonth}
              onChange={(e) => onMonthSelect(e.target.value)}
              disabled={archiveBusy}
              title="月を切り替えます。過去月（確定済み）は読み取り専用です。"
            >
              {monthOptions.map((o) => (
                <option key={o.month} value={o.month}>
                  {formatMonth(o.month)}{monthOptionSuffix(o, state.targetMonth, monthLocked)}
                </option>
              ))}
            </select>
            {isArchive && <span className="chip blue dot">過去月・読み取り専用</span>}
            {!isArchive && currentEntry && (
              monthLocked
                ? <span className="chip green dot">確定済み・編集不可</span>
                : <span className="chip amber dot">訂正中</span>
            )}
          </div>
          <div className="page-sub muted">
            {formatMonth(viewingMonth)} ／ 全 {statements.length} 件
            {selected.size > 0 ? ` ／ ${selected.size} 件 選択中` : ""}
            {!isArchive && statements.length > 0 ? ` ／ 確定 ${confirmedCount}/${statements.length}` : ""}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={() => onNav("home")}><Ic.chevronL size={13} /> ホーム</button>
        {share.mode !== "off" && (
          <button
            className="btn"
            onClick={handleRefreshShared}
            disabled={refreshing || archiveBusy}
            title="他の方が保存した最新の内容と、月の確定状況を取り直します（通常は20秒ごとに自動で揃います）"
          >
            <Ic.history size={14} /> {refreshing ? "更新中..." : "最新の状態にする"}
          </button>
        )}
        <button
          className="btn"
          onClick={() => setBulkOpen(true)}
          disabled={bulkTargets.length === 0}
          title="選択した明細（未選択なら表示中すべて）の支払通知書・売上明細表を、1つのPDFにまとめて出力します"
        >
          <Ic.doc size={14} /> まとめてPDF ({bulkTargets.length})
        </button>
        <button
          className="btn"
          onClick={handleDownloadAccountingCsv}
          disabled={statements.length === 0}
          title="表示中の月の支払明細から、MF会計（マネーフォワード会計）仕訳取込用のCSVをダウンロードします"
        >
          <Ic.download size={14} /> 会計取込CSV
        </button>
        {isArchive && selected.size > 0 && (
          <button className="btn ghost" onClick={clearSelection}>選択解除 ({selected.size})</button>
        )}
        {!isArchive && monthLocked && (
          <>
            <button
              className="btn"
              onClick={handlePublish}
              disabled={publishBusy || statements.length === 0}
              title="選択した明細（未選択なら表示中すべて）をドライバー承認用に発行し、配布URLを作ります"
            >
              <Ic.send size={14} /> {publishBusy ? "発行中..." : `承認用に発行 (${publishTargets.length})`}
            </button>
            <button className="btn" onClick={handleUnlock} title="確定した月を訂正できる状態に戻します">
              <Ic.edit size={14} /> この月を訂正する
            </button>
          </>
        )}
        {!isArchive && !monthLocked && (
          <>
            <button
              className="btn"
              onClick={() => setManualOpen(true)}
              title="CSVに出ないドライバー（橋本光弘様など）の通知書を手動で作成します"
            >
              <Ic.plus size={14} /> 手動作成
            </button>
            {selected.size > 0 && (
              <>
                <button className="btn ghost" onClick={clearSelection}>選択解除</button>
                <button className="btn" onClick={confirmSelected}><Ic.check size={14} /> 選択を確定 ({selected.size})</button>
              </>
            )}
            <button
              className="btn"
              onClick={handlePublish}
              disabled={publishBusy || statements.length === 0}
              title="選択した明細（未選択なら表示中すべて）をドライバー承認用に発行し、配布URLを作ります"
            >
              <Ic.send size={14} /> {publishBusy ? "発行中..." : `承認用に発行 (${publishTargets.length})`}
            </button>
            <button
              className="btn primary"
              onClick={handleFinalize}
              disabled={archiveBusy || !allConfirmed}
              title={allConfirmed ? "この月を確定してスナップショットを保存します" : `全明細の確定が必要です（残り ${statements.length - confirmedCount} 件）`}
            >
              <Ic.checkCircle size={14} /> {archiveBusy ? "保存中..." : "月を確定"}
            </button>
          </>
        )}
      </div>

      {flash && (
        <div className={"toast " + (flash.ok ? "success" : "warn")}>
          {flash.ok ? <Ic.checkCircle size={14} /> : <Ic.alert size={14} />}
          <div style={{ flex: 1 }}>{flash.msg}</div>
          <button className="btn sm ghost" onClick={() => setFlash(null)}><Ic.x size={14} /></button>
        </div>
      )}

      {/* 過去月（読み取り専用）バナー */}
      {isArchive && archivedRecord && (
        <div className="archive-banner">
          <div className="row gap-8" style={{ alignItems: "center", flex: 1, flexWrap: "wrap" }}>
            <Ic.clock size={16} />
            <span><b>{formatMonth(archivedRecord.month)}</b> は確定済みの過去月です（読み取り専用）。左のチェックで選んだ方だけを「まとめてPDF」で出力できます。<br />
              <span className="tiny">1人だけ直すときは、その方の明細を開いて「この人だけ訂正」を押してください（作業中の月と他の方には影響しません）。月全体を直すときだけ右の「再オープン」を使います。</span></span>
            {(archivedRecord.corrections?.length ?? 0) > 0 && (
              <span className="chip amber" title={archivedRecord.corrections!.map((c) => `${new Date(c.at).toLocaleString("ja-JP")} ${c.by}：${c.driverName} ¥${c.before.netTotal.toLocaleString()} → ¥${c.after.netTotal.toLocaleString()}`).join("\n")}>
                確定後の個別訂正 {archivedRecord.corrections!.length} 件
              </span>
            )}
            {archivedRecord.finalizedAt && (
              <span className="tiny muted">確定 {new Date(archivedRecord.finalizedAt).toLocaleString("ja-JP")} ・ {archivedRecord.finalizedBy || "—"}</span>
            )}
          </div>
          <button className="btn sm" onClick={handleReopen} disabled={archiveBusy}><Ic.edit size={13} /> この月を訂正（再オープン）</button>
        </div>
      )}

      {!isArchive && monthLocked && (
        <div className="archive-banner">
          <div className="row gap-8" style={{ alignItems: "center", flex: 1, flexWrap: "wrap" }}>
            <Ic.checkCircle size={16} />
            <span><b>{formatMonth(state.targetMonth)}</b> は確定済みです。内容は編集できません（金額・確定状態・通知書の追加削除）。</span>
            <span className="tiny muted">支払通知書のPDF出力・承認用の発行・CSVのダウンロードはこのまま行えます。</span>
          </div>
          <button className="btn sm" onClick={handleUnlock}><Ic.edit size={13} /> この月を訂正する</button>
        </div>
      )}

      {!isArchive && !monthLocked && currentEntry && (
        <div className="toast warn">
          <Ic.alert size={14} />
          <div className="col gap-4">
            <div><b>{formatMonth(state.targetMonth)} を訂正中です（編集できる状態）</b></div>
            <div className="tiny">
              {unlockInfo.at ? `${new Date(unlockInfo.at).toLocaleString("ja-JP")} に${unlockInfo.by ? ` ${unlockInfo.by} が` : ""}訂正を開始。` : ""}
              訂正が終わったら「月を確定」を押して保存し直してください。押すまで、保存済みの内容は訂正前のままです。
            </div>
          </div>
        </div>
      )}

      {periodMismatch && (
        <div className="toast err">
          <Ic.alert size={14} />
          <div className="col gap-4">
            <div><b>取込CSVの対象月が揃っていません（ヤマト {formatMonth(periodMismatch.yamatoMonth)} ／ 佐川 {formatMonth(periodMismatch.sagawaMonth)}）</b></div>
            <div className="tiny">
              いま表示している金額は2か月分が混ざっています。「CSV取込」で前月分を削除し、当月分をアップロードし直してください。
            </div>
          </div>
        </div>
      )}

      {statements.length === 0 && !isArchive && (
        <div className="toast info">
          <Ic.info size={14} />
          まだ支払明細がありません。「CSV取込」からヤマト・佐川のCSVをアップロードしてください。
        </div>
      )}

      {(unmatchedNames.length > 0 || candidates.length > 0) && (
        <div className="card match-card">
          <div className="match-card-head"><Ic.alert size={17} /> 突合の確認事項</div>
          {unmatchedNames.length > 0 && (
            <div className="match-row">
              <span className="match-row-label">マスター未登録 {unmatchedNames.length}名</span>
              <div className="match-chips">
                {unmatchedNames.map((name) => (
                  <button key={name} className="match-chip" onClick={() => onAddMaster?.(name)} title="クリックするとマスター追加フォームを開きます">
                    <Ic.plus size={13} /> {name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {candidates.length > 0 && (
            <div className="match-note">
              名寄せ候補 {candidates.length} 名：{candidates.slice(0, 5).map((m) => `${m.csvName}→${m.candidates[0]?.name}`).join("、")}
              {candidates.length > 5 ? " …" : ""}
            </div>
          )}
        </div>
      )}

      {groupNotes.length > 0 && (
        <div className="group-note-card">
          {groupNotes.map(({ rule, members }) => (
            <div className="group-note-row" key={rule.id}>
              <Ic.info size={13} /> グループ計上 {members.length}名（{rule.groupName}）：
              {members.slice(0, 5).join("、")}
              {members.length > 5 ? " …" : ""}
              （各ドライバーの個人通知書とは別に、グループ宛通知書にも計上）
            </div>
          ))}
        </div>
      )}

      {!isArchive && excludedEntries.length > 0 && (
        <div className="group-note-card">
          <div className="group-note-row">
            <Ic.info size={13} /> 配信除外中 {excludedEntries.length}名（0円の通知書を作らない設定）：
            {excludedEntries.map((e) => (
              <span key={e.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, marginRight: 6 }}>
                {e.name}
                <span className="tiny muted">{e.qty > 0 ? `（今月実績 ${e.qty.toLocaleString("ja-JP")}個）` : "（今月実績なし）"}</span>
                {!monthLocked && <button
                  className="btn sm ghost"
                  style={{ height: 22, padding: "0 8px", fontSize: 11 }}
                  onClick={() => restoreExcluded(e.id, e.name)}
                  title="配信除外を解除します。CSV取込済みなら通知書がすぐ復活します"
                >
                  戻す
                </button>}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="status-tabs">
        <button className={"st-tab" + (statusFilter === "" ? " active" : "")} onClick={() => setStatusFilter("")}>
          すべて <span className="st-count">{statements.length}</span>
        </button>
        {(["draft", "confirmed", "hold"] as const).map((k) => (
          <button key={k} className={"st-tab" + (statusFilter === k ? " active" : "")} onClick={() => setStatusFilter(k)}>
            <span className={"st-dot " + k} />
            {labelOfStatus(k)} <span className="st-count">{counts[k] || 0}</span>
          </button>
        ))}
        <button className={"st-tab" + (statusFilter === "attention" ? " active" : "")} onClick={() => setStatusFilter("attention")}>
          <span className="st-dot error" />
          要確認 <span className="st-count">{attentionCount}</span>
        </button>
        <div style={{ flex: 1 }} />
        <div className="sum-cells">
          <SumCell label="売上合計" value={yen(totals.sales)} />
          <SumCell label="控除合計" value={totals.deduct > 0 ? "△" + yen(totals.deduct) : "0"} neg={totals.deduct > 0} />
          <SumCell label="差引支給合計" value={yen(totals.net)} strong />
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-wrap" style={{ flex: "0 0 320px" }}>
          <Ic.search size={14} />
          <input className="input search" placeholder="氏名 / IDで検索" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <select className="select" value={carrierFilter} onChange={(e) => setCarrierFilter(e.target.value as Carrier | "")}>
          <option value="">キャリア：すべて</option>
          <option value="yamato">ヤマト</option>
          <option value="sagawa">佐川</option>
        </select>
        <div style={{ flex: 1 }} />
        <span className="tiny muted num">{filtered.length} 件</span>
      </div>

      {/* 携帯用の全選択バー。カード表示では表の見出し行（thead）を隠すので、
          その中にあった全選択チェックが押せなくなる。一覧の外に出して残す
          （「全員から1名だけ外す」が個別タップの繰り返しになるのを防ぐ。
          Astra レビュー 2026-09-13 の指摘） */}
      <div className="select-all-bar">
        <label className="select-all-label">
          <input
            type="checkbox"
            checked={allFilteredSelected}
            onChange={toggleAll}
            title="表示中をすべて選択"
          />
          <span>表示中をすべて選択</span>
        </label>
        <span className="tiny muted">
          {selected.size > 0 ? `${selected.size} 件 選択中` : `${filtered.length} 件`}
        </span>
      </div>

      <div className="list-wrap">
        <div className="card list-card tbl-cards-wrap">
          <div className="tbl-scroll">
            <table className="tbl list-tbl tbl-cards">
              <thead>
                <tr>
                  <th className="check-col">
                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} title="表示中をすべて選択" />
                  </th>
                  <th>ID</th>
                  <th>氏名</th>
                  <th>キャリア</th>
                  <th>支店</th>
                  <th className="num">売上合計</th>
                  <th className="num">控除合計</th>
                  <th className="num">差引支給額</th>
                  <th>ステータス</th>
                  <th>承認</th>
                  {!isArchive && <th style={{ width: 40 }} />}
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const sel = selected.has(keyOf(s));
                  const [bg, fg] = avatarColor(s.driverName);
                  return (
                    <tr key={s.driverId} className={sel ? "selected" : ""} onClick={() => onSelect(s.driverId)} style={{ cursor: "pointer" }}>
                      <td className="check-col" onClick={(e) => { e.stopPropagation(); toggleOne(s); }}>
                        <input type="checkbox" checked={sel} onChange={() => {}} />
                      </td>
                      <td className="mono muted" data-label="ID">{s.driverId}</td>
                      <td className="card-title-cell">
                        <div className="row gap-8" style={{ alignItems: "center" }}>
                          <span className="initial-bubble" style={{ background: bg, color: fg }}>{s.driverName[0]}</span>
                          <div className="col">
                            <span style={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 5 }}>
                              {s.driverName}
                              {needsAttention(s) && <span title={s.warnings.join(" / ") || "売上が0です"} style={{ color: "var(--red)", display: "inline-flex" }}><Ic.alert size={14} /></span>}
                            </span>
                            {!s.invoiceNo && <span className="tiny" style={{ color: "var(--amber)", fontWeight: 700 }}>インボイス未登録</span>}
                          </div>
                        </div>
                      </td>
                      <td data-label="キャリア">
                        <div className="row gap-4" style={{ flexWrap: "wrap" }}>
                          {(s.carriers ?? [s.carrier]).map((c) => (
                            <span key={c} className={`chip carrier-${c}`}>{CARRIER_LABEL[c]}</span>
                          ))}
                        </div>
                      </td>
                      <td className="muted" data-label="支店">{s.branch}</td>
                      <td className="num" data-label="売上合計"><b>{yen(s.salesTotal)}</b></td>
                      <td className={"num" + (s.deductTotal > 0 ? " neg" : " muted")} data-label="控除合計">{s.deductTotal > 0 ? "△" + yen(s.deductTotal) : "0"}</td>
                      <td className="num net-cell" data-label="差引支給額" style={{ fontWeight: 800 }}>{yen(s.netTotal)}</td>
                      <td data-label="ステータス"><StatusChip s={s.status} /></td>
                      <td data-label="承認" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const a = approvalMap.get(s.driverId);
                          if (!a) return <span className="tiny muted">未発行</span>;
                          return (
                            <span className="tiny" title={a.question ? `質問: ${a.question}` : `発行 ${new Date(a.publishedAt).toLocaleString("ja-JP")}`}
                              style={{ padding: "1px 8px", borderRadius: 999, fontWeight: 700, background: APPROVAL_COLOR[a.status] + "22", color: APPROVAL_COLOR[a.status] }}>
                              {APPROVAL_LABEL[a.status]}
                            </span>
                          );
                        })()}
                      </td>
                      {!isArchive && (
                        <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                          {isZeroStatement(s) &&
                            !monthLocked &&
                            !s.driverId.startsWith("group:") && (
                              <button
                                className="btn sm ghost"
                                style={{ height: 24, padding: "0 8px" }}
                                onClick={() => excludeZero(s)}
                                title="0円の通知書を配信対象から外します（マスタからは消えません）"
                              >
                                <Ic.x size={13} /> 除外
                              </button>
                            )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: 14, fontSize: 12, color: "var(--ink-3)" }}>
        <span className="hide-sp">行をクリックすると</span><span className="only-sp">カードをタップすると</span> <b style={{ color: "var(--blue)" }}>明細詳細</b> を開きます{isArchive ? "（過去月は読み取り専用）" : ""}
      </div>

      {publishResult && <PublishResultDialog items={publishResult} onClose={() => setPublishResult(null)} />}

      {bulkOpen && (
        <BulkPrintModal
          count={bulkTargets.length}
          scopeLabel={selected.size > 0 ? `選択中の ${bulkTargets.length} 名` : `表示中の ${bulkTargets.length} 名`}
          monthLabel={formatMonth(viewingMonth)}
          busy={bulkBusy}
          onPrint={startBulkPrint}
          onClose={() => { if (!bulkBusy) setBulkOpen(false); }}
        />
      )}

      {/* 一括印刷ポータル（印刷中だけマウントする） */}
      {bulkPrint && typeof document !== "undefined" && document.getElementById("print-root")
        ? createPortal(
            <BulkPrintSheets statements={bulkPrint.items} mode={bulkPrint.mode} />,
            document.getElementById("print-root") as HTMLElement
          )
        : null}
    </div>
  );
}

// まとめてPDF出力のダイアログ（帳票の種類を選ぶ＋操作の案内）
function BulkPrintModal({
  count,
  scopeLabel,
  monthLabel,
  busy,
  onPrint,
  onClose,
}: {
  count: number;
  scopeLabel: string;
  monthLabel: string;
  busy: boolean;
  onPrint: (mode: "notice" | "sales") => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="modal-head">
          <div className="card-title">まとめてPDF出力</div>
          <button className="btn btn-icon-only ghost" onClick={onClose} disabled={busy}><Ic.x size={16} /></button>
        </div>
        <div className="modal-body col gap-12">
          <div className="toast info" style={{ margin: 0 }}>
            <Ic.info size={14} />
            <div className="tiny">
              <b>{monthLabel}</b> の <b>{scopeLabel}</b> ぶんを、1つのPDFにまとめて出力します（1人1ページ〜／一覧の並び順）。<br />
              印刷ダイアログが開いたら、送信先（プリンター）を <b>「PDFに保存」</b> にして保存してください。
            </div>
          </div>
          {count > 60 && (
            <div className="toast warn" style={{ margin: 0 }}>
              <Ic.alert size={14} />
              <div className="tiny">{count} 名ぶんのため、印刷ダイアログが開くまで十数秒かかることがあります。そのままお待ちください。</div>
            </div>
          )}
          <div className="col gap-8">
            <button className="btn primary" onClick={() => onPrint("notice")} disabled={busy} style={{ height: 44 }}>
              <Ic.download size={15} /> {busy ? "準備中..." : `支払通知書をまとめて出力 (${count})`}
            </button>
            <button className="btn" onClick={() => onPrint("sales")} disabled={busy} style={{ height: 44 }}>
              <Ic.doc size={15} /> {busy ? "準備中..." : `売上明細表をまとめて出力 (${count})`}
            </button>
          </div>
          <div className="tiny muted">
            一部の方だけ出したいときは、いったんこの画面を閉じて一覧のチェックボックスで選んでから、もう一度押してください。
          </div>
        </div>
      </div>
    </div>
  );
}

function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function SumCell({ label, value, neg, strong }: { label: string; value: string; neg?: boolean; strong?: boolean }) {
  return (
    <div className="sum-cell">
      <div className="sum-cell-label">{label}</div>
      <div className={"sum-cell-value num" + (neg ? " neg" : "") + (strong ? " strong" : "")}>
        {value} <span className="sum-cell-unit">円</span>
      </div>
    </div>
  );
}

function PublishResultDialog({ items, onClose }: { items: PublishedItem[]; onClose: () => void }) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    } catch {
      alert("コピーに失敗しました。URLを手動で選択してください。");
    }
  };
  const allText = items.map((i) => `${i.driverName}\t${i.url}`).join("\n");

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-head">
          <div className="card-title">承認用URLを発行しました（{items.length} 名）</div>
          <button className="btn btn-icon-only ghost" onClick={onClose}><Ic.x size={16} /></button>
        </div>
        <div className="modal-body col gap-12">
          <div className="toast info">
            <Ic.info size={14} />
            <div className="tiny">
              各ドライバーに<b>本人専用のURL</b>です。LINE等で各人へ送ってください。ドライバーがスマホで開くと明細を確認・承認できます。
            </div>
          </div>
          <div className="row gap-8">
            <button className="btn" onClick={() => copy(allText, "__all__")}>
              <Ic.doc size={14} /> {copied === "__all__" ? "コピーしました" : "全員ぶん（氏名+URL）をコピー"}
            </button>
          </div>
          <div className="tbl-scroll" style={{ maxHeight: 360 }}>
            <table className="tbl">
              <thead><tr><th>氏名</th><th>URL</th><th></th></tr></thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.driverId}>
                    <td>
                      {it.driverName}
                      {it.emailSent && <span className="chip green" style={{ marginLeft: 6 }}>✉ 送信済</span>}
                      {it.emailError && <span className="chip red" style={{ marginLeft: 6 }} title={it.emailError}>✉ 失敗</span>}
                    </td>
                    <td className="tiny mono" style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.url}</td>
                    <td><button className="btn sm ghost" onClick={() => copy(it.url, it.driverId)}>{copied === it.driverId ? "✓" : "コピー"}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn primary" onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>
  );
}

function labelOfStatus(k: Statement["status"]) {
  switch (k) {
    case "draft": return "未確定";
    case "confirmed": return "確定";
    case "hold": return "保留";
    case "error": return "要確認";
  }
}

function dedupeNames(names: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const n of names) if (!seen.has(n)) { seen.add(n); out.push(n); }
  return out;
}

function StatusChip({ s }: { s: Statement["status"] }) {
  if (s === "confirmed") return <span className="chip green dot">確定</span>;
  if (s === "hold") return <span className="chip amber dot">保留</span>;
  if (s === "error") return <span className="chip red dot">要確認</span>;
  return <span className="chip dot">未確定</span>;
}
